// A product-analytics event (data-model.md "events"; instrumentation-and-ingest §2).
//
// One standard tracking plan, not raw autocapture: a small, fixed set of well-named
// events, each tied to an outcome. Naming rule: lowercase, underscores, verb_noun. NO
// personal data in `props` by default; ids are hashed anonymous ids, never a real user.
// Tenant-scoped by `accountId`/`appId`, both assigned server-side from the write key -
// a client cannot address another tenant (see ports/ingest.ts).
import { z } from "zod";
import { AccountId, AppId, EventId } from "../ids.js";
import { JsonObject, Timestamp } from "./common.js";
/**
 * A `verb_noun` event name: lowercase alphanumeric segments joined by underscores, at
 * least one underscore. Enforces the naming rule that prevents data debt (sign-up vs
 * sign_up vs user_signup splitting one metric across three lines).
 */
export const EventName = z
    .string()
    .regex(/^[a-z][a-z0-9]*(?:_[a-z0-9]+)+$/, "must be lowercase verb_noun");
/** The standard core lifecycle taxonomy every app emits (instrumentation-and-ingest §2). */
export const CORE_EVENT_NAMES = [
    "session_started",
    "signup_started",
    "signup_completed",
    "onboarding_step_viewed",
    "onboarding_completed",
    "activation_reached",
    "feature_used",
    "flow_abandoned",
    "error_encountered",
    "subscription_started",
    "subscription_cancelled",
    // Payment apps also emit:
    "checkout_started",
    "payment_completed",
    "payment_failed",
];
export const Event = z.object({
    id: EventId,
    accountId: AccountId,
    appId: AppId,
    name: EventName,
    /** Event properties (jsonb). No PII by construction (instrumentation-and-ingest §7). */
    props: JsonObject,
    /** Hashed anonymous user id; null before identity (e.g. session_started). */
    anonUser: z.string().min(1).nullable(),
    session: z.string().min(1).nullable(),
    route: z.string().min(1).nullable(),
    occurredAt: Timestamp,
});
//# sourceMappingURL=event.js.map